import {useContext} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {atomIconSizes} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import {leadPhoneCallColors, leadPhoneCallDesigns} from '@adv-mt/mt-lead-phone-call'
import IconMailOutline from '@adv-mt/mt-svg-icons/lib/MailOutline.js'
import {Button, BUTTON_INTENTS, BUTTON_SHAPES, BUTTON_SIZES, BUTTON_VARIANTS} from '@adv-mt/ui/button'

import PhoneAction from '../ContactActions/PhoneAction.js'
import WhatsappButton from '../ContactActions/WhatsappButton.js'
import {ContactSellerV2ConfigProvider, pickContactSellerV2Config} from '../context/ContactSellerV2Context.js'
import {availableActionsPropType, CONTACT_ACTION_IDS, defaultAvailableActions} from '../contracts.js'
import {scrollToSection} from '../helpers/scrollToSection.js'
import deriveLayout from './deriveLayout.js'

export const BASE_CLASS = 'mt-StickyContactBar'

export const CONTACT_DRAWER_TYPE = 'contactForm'

const ACTION_COMPONENTS = {
  [CONTACT_ACTION_IDS.PHONE]: PhoneAction,
  [CONTACT_ACTION_IDS.WHATSAPP]: WhatsappButton
}

const StickyContactBar = ({
  availableActions = defaultAvailableActions,
  canariasBanner,
  hidePhone = false,
  isDrawerOpen = false,
  isSticky = false,
  onOpenDrawer,
  onSendMessageClick,
  ...props
}) => {
  const {domain, i18n} = useContext(Context)
  const {AD_DETAIL_PAGE_SECTION_IDS} = domain.get('config').get('COMMON').AD_DETAIL_PAGE_SECTIONS
  const sectionId = AD_DETAIL_PAGE_SECTION_IDS.SELLER

  const {layout, slots} = deriveLayout(
    hidePhone ? {...availableActions, [CONTACT_ACTION_IDS.PHONE]: {available: false}} : availableActions
  )

  const contactLabel = i18n.t('CONTACT_SELLER_V2.FORM.SUBMIT_CTA')

  const haspopup = onOpenDrawer ? 'dialog' : undefined
  const expanded = onOpenDrawer ? isDrawerOpen : undefined

  const handleContactClick = () => {
    onSendMessageClick?.()

    if (onOpenDrawer) {
      onOpenDrawer({
        type: CONTACT_DRAWER_TYPE,
        data: {adId: props.adId, contractId: props.contractId, phone: props.phone}
      })
      return
    }

    scrollToSection(sectionId)
  }

  return (
    <ContactSellerV2ConfigProvider value={pickContactSellerV2Config(props)}>
      <div
        aria-hidden={!isSticky}
        className={cx(BASE_CLASS, `${BASE_CLASS}--${layout}`, {[`${BASE_CLASS}--visible`]: isSticky})}
      >
        {canariasBanner && <div className={`${BASE_CLASS}-canariasBanner`}>{canariasBanner}</div>}
        {slots.map(({id, iconOnly}) => {
          if (id === CONTACT_ACTION_IDS.MESSAGE) {
            return (
              <div className={`${BASE_CLASS}-slot`} key={id}>
                <Button
                  aria-expanded={expanded}
                  aria-haspopup={haspopup}
                  aria-label={iconOnly ? contactLabel : undefined}
                  fullWidth
                  intent={BUTTON_INTENTS.ACCENT}
                  onClick={handleContactClick}
                  shape={BUTTON_SHAPES.CIRCLE}
                  size={BUTTON_SIZES.LARGE}
                  variant={BUTTON_VARIANTS.SOLID}
                >
                  {iconOnly ? <IconMailOutline size={atomIconSizes.medium} /> : contactLabel}
                </Button>
              </div>
            )
          }

          const Component = ACTION_COMPONENTS[id]

          return (
            <div className={`${BASE_CLASS}-slot`} key={id}>
              <Component
                {...(id === CONTACT_ACTION_IDS.PHONE
                  ? {color: leadPhoneCallColors.ACCENT, design: leadPhoneCallDesigns.SOLID}
                  : {})}
                iconOnlyCapable={iconOnly}
              />
            </div>
          )
        })}
      </div>
    </ContactSellerV2ConfigProvider>
  )
}

StickyContactBar.displayName = 'StickyContactBar'

StickyContactBar.propTypes = {
  availableActions: availableActionsPropType,
  adId: PropTypes.string,
  canariasBanner: PropTypes.node,
  contractId: PropTypes.string,
  phone: PropTypes.string,
  hidePhone: PropTypes.bool,
  isSticky: PropTypes.bool,
  isDrawerOpen: PropTypes.bool,
  multiprovince: PropTypes.string,
  onLeadPhoneCall: PropTypes.func,
  onOpenDrawer: PropTypes.func,
  onSendMessageClick: PropTypes.func,
  onWhatsappError: PropTypes.func
}

export default StickyContactBar
